import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../layouts/PageTitleContext';
import EntityTable from '../components/Listing/EntityTable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUserPlus,
    faEllipsisH,
    faTrash,
    faUser,
    faEdit,
    faNotesMedical,
    faEnvelope,
} from '@fortawesome/free-solid-svg-icons';
import PatientModal from '../components/Modal/PatientModal';
import ConfirmModal from '../components/Modal/ConfirmModal';
import { NavLink, useNavigate } from 'react-router-dom';
import PatientImage from '../components/Patient/PatientImage';
import useBreadcrumbSetup from '../components/useBreadcrumbSetup';
import InputText from '../components/Form/Fields/InputText';
import FabGroup from "../components/FabGroup";
import InputInsurance from "../components/Form/Fields/InputInsurance";
import Select from '../components/Form/Fields/Select';
import { getPatientIndicatorOptions } from '../config/PatientIndicator';
import { getRegimeOptions } from '../config/Regime';
import { useAuth } from '../context/AuthProvider';
import { API_BASE_URL } from '../api';
import { formatPrice } from '../utils/FormatPrice';
import './Patients.scss';

export default function Patients() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { hasRight } = useAuth();
    const canCreate = hasRight('PATIENTS_CREATE');
    const canEdit = hasRight('PATIENTS_EDIT');
    const canDelete = hasRight('PATIENTS_DELETE');
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    // ids to delete + the patient row for a single delete (its unpaid sales are shown in the warning)
    const [pendingDelete, setPendingDelete] = useState({ ids: [], patient: null });
    const [isDeleting, setIsDeleting] = useState(false);
    const { setTitle, setTitleIcon } = usePageTitle();
    const tableRef = useRef(null);

    useEffect(() => {
        setTitle(`${t('Patients')}<span style="display:block;font-size:11px;font-weight:400;color:#9ca3af;margin-top:5px;">${t('patients.subtitle')}</span>`);
        document.title = t('Patients');
        setTitleIcon('ti-users');
        return () => setTitleIcon(null);
    }, [setTitle, setTitleIcon, t]);

    useBreadcrumbSetup([{ label: t('Patients'), url: "/patients" }]);

    const handleNewPatient = () => {
        setSelectedPatient(null);
        setShowModal(true);
    };

    const requestDelete = (ids, patient = null) => {
        setPendingDelete({ ids, patient });
        setShowDeleteModal(true);
    };

    const closeDeleteModal = () => {
        if (isDeleting) return;
        setShowDeleteModal(false);
        setPendingDelete({ ids: [], patient: null });
    };

    // Same behaviour as the patient page's "Supprimer": the dialog stays open with a spinner
    // until the deletion is done, then closes; an error keeps the list as it was.
    const handleConfirmDelete = async () => {
        const { ids } = pendingDelete;
        setIsDeleting(true);
        try {
            await Promise.all(ids.map(id =>
                axios.delete(`${API_BASE_URL}/api/v1/deletePatient/${id}`, { withCredentials: true })
            ));
            toast.success(ids.length > 1
                ? t('patients.toast.deleted_multiple', { count: ids.length })
                : t('patients.toast.deleted_single')
            );
        } catch (err) {
            console.error('Failed to delete patient:', err);
            toast.error(t('patients.toast.delete_error'));
        } finally {
            setIsDeleting(false);
            setShowDeleteModal(false);
            setPendingDelete({ ids: [], patient: null });
            tableRef.current?.reload();
        }
    };

    // Row actions (table "•••" menu and card menu): each one goes where it says — the patient
    // page's overview / profile form / sales tab, the mail client, or the delete confirmation.
    const renderPatientActions = (row) => (
        <ul className="dropdown-menu dropdown-menu-end">
            <li>
                <NavLink to={`/patient/${row.id}`} className="dropdown-item">
                    <FontAwesomeIcon icon={faUser} className="me-2 text-muted" />
                    {t('patients.action.view_profile')}
                </NavLink>
            </li>
            {canEdit && (
                <li>
                    <NavLink to={`/patient/${row.id}/profile`} className="dropdown-item">
                        <FontAwesomeIcon icon={faEdit} className="me-2 text-muted" />
                        {t('Edit')}
                    </NavLink>
                </li>
            )}
            <li>
                <NavLink to={`/patient/${row.id}/orders`} className="dropdown-item">
                    <FontAwesomeIcon icon={faNotesMedical} className="me-2 text-muted" />
                    {t('Orders')}
                </NavLink>
            </li>
            <li><hr className="dropdown-divider" /></li>
            <li>
                {row.email ? (
                    <a href={`mailto:${row.email}`} className="dropdown-item">
                        <FontAwesomeIcon icon={faEnvelope} className="me-2 text-muted" />
                        {t('patients.action.send_email')}
                    </a>
                ) : (
                    <span
                        className="dropdown-item disabled"
                        aria-disabled="true"
                        data-tooltip-id="tooltip"
                        data-tooltip-content="Aucun email renseigné pour ce patient"
                    >
                        <FontAwesomeIcon icon={faEnvelope} className="me-2 text-muted" />
                        {t('patients.action.send_email')}
                        <small className="ms-1 text-muted">(aucun email)</small>
                    </span>
                )}
            </li>
            {canDelete && (
                <>
                    <li><hr className="dropdown-divider" /></li>
                    <li>
                        <button className="dropdown-item danger" onClick={() => requestDelete([row.id], row)}>
                            <FontAwesomeIcon icon={faTrash} className="me-2" />
                            {t('Delete')}
                        </button>
                    </li>
                </>
            )}
        </ul>
    );

    // Payment status chip (table and cards): "● À jour", or just "● 69,90 dhs" in red for a late
    // payer — the red tint says "en retard" (in the tooltip), the amount is what matters
    const PatientStatus = ({ row }) => {
        const ok = row.remainingToPay === 0;
        const price = formatPrice(row.remainingToPay);
        const cut = price.lastIndexOf(' ');
        return (
            <span
                className={`patient-status ${ok ? 'patient-status--ok' : 'patient-status--late'}`}
                data-tooltip-id="tooltip"
                data-tooltip-content={ok ? t('patients.status.up_to_date') : `${t('patients.status.overdue')} · ${price}`}
            >
                <span className="patient-status__dot" />
                {ok ? t('patients.status.up_to_date') : (
                    <span className="patient-status__amount">
                        {cut > 0 ? price.slice(0, cut) : price}
                        {cut > 0 && <span className="patient-status__unit">{price.slice(cut + 1)}</span>}
                    </span>
                )}
            </span>
        );
    };

    // Card-view design for this listing (Table's enableCardView feature) — mirrors the
    // table columns' key info (status, CIN, régime, assurance, ville) in a compact tile.
    const renderPatientCard = (row) => {
        const defaultAddress = row.addresses?.find(a => a.default);

        return (
            <div className="card h-100 shadow-sm" style={{ cursor: 'pointer' }} onClick={() => navigate(`/patient/${row.id}`)}>
                <div className="card-body d-flex flex-column" style={{ padding: 14, gap: 6 }}>
                    <span style={{ alignSelf: 'flex-start' }}><PatientStatus row={row} /></span>

                    <div className="d-flex align-items-center" style={{ gap: 8, marginTop: 6 }}>
                        <PatientImage patient={row} size={44} />
                        <div style={{ minWidth: 0 }}>
                            <NavLink
                                to={`/patient/${row.id}`}
                                onClick={(e) => e.stopPropagation()}
                                style={{ fontSize: 13.5, fontWeight: 600, color: "#111827", display: 'block' }}
                            >
                                {row.firstName} {row.lastName}
                            </NavLink>
                            <span style={{ fontSize: 11.5, color: "#9ca3af" }}>
                                {row.sex == 0 ? t('patients.gender.male') : t('patients.gender.female')}
                            </span>
                        </div>
                    </div>

                    {row.cin && row.cin !== "N/A" && (
                        <div style={{ fontSize: 11.5, color: "#6b7280" }}>
                            <span className="cell-mono">{row.cin}</span>
                        </div>
                    )}

                    {row.healthInsurance ? (
                        <div className="d-flex align-items-center gap-2" style={{ fontSize: 11.5, color: "#374151" }}>
                            <img src={row.healthInsuranceLogo} style={{ width: 18, height: 18, borderRadius: 4, objectFit: "cover" }} alt="" />
                            {row.healthInsuranceName}
                        </div>
                    ) : row.regime && row.regime !== "N/A" && (
                        <div style={{ fontSize: 11.5, color: "#374151" }}>{row.regime}</div>
                    )}

                    {defaultAddress && (
                        <div style={{ fontSize: 11.5, color: "#9ca3af" }}>{defaultAddress.city}</div>
                    )}

                    <div className="d-flex align-items-center justify-content-between" style={{ marginTop: 'auto', paddingTop: 6 }}>
                        <span style={{
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                            background: row.ordersCount > 0 ? "#eff6ff" : "#f9fafb",
                            color: row.ordersCount > 0 ? "#1d4ed8" : "#9ca3af",
                            fontWeight: 600, fontSize: 12,
                            padding: "3px 9px", borderRadius: 999, minWidth: 28,
                        }}>
                            {row.ordersCount} {t('Orders')}
                        </span>

                        <div className="cell-dropdown" onClick={(e) => e.stopPropagation()}>
                            <button
                                type="button"
                                className="btn btn-sm btn-light dropdown-toggle"
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                            >
                                <FontAwesomeIcon icon={faEllipsisH} />
                            </button>
                            {renderPatientActions(row)}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const columns = [
        {
            Header: "#",
            accessor: "id",
            sortable: true,
            className: 'text-center',
            filterable: { component: InputText },
            Cell: (row) => <span style={{ fontSize: 14, fontWeight: 500, color: "#9ca3af" }}>{row.id}</span>,
        },
        {
            Header: t('Patient'),
            accessor: "firstName",
            skeleton: 'media', // loading placeholder: avatar + two lines
            sortable: true,
            filterable: { component: InputText },
            Cell: (row) => (
                <div className="d-flex align-items-center gap-3">
                    <PatientImage patient={row} size={40} />
                    <div>
                        <NavLink to={`/patient/${row.id}`} className="cell-name">
                            {row.firstName} {row.lastName}
                        </NavLink>
                        <span className="cell-sub">
                            {row.sex == 0 ? t('patients.gender.male') : t('patients.gender.female')}
                        </span>
                    </div>
                </div>
            ),
        },
        {
            Header: t('patients.column.status'),
            accessor: "isPaymentUpToDate",
            filterable: { component: Select, options: getPatientIndicatorOptions() },
            Cell: (row) => <PatientStatus row={row} />,
        },
        {
            Header: t('patients.column.cni'),
            accessor: "cin",
            filterable: { component: InputText },
            Cell: (row) => (!row.cin || row.cin === "N/A")
                ? <span className="cell-muted">—</span>
                : <span className="cell-mono">{row.cin}</span>,
        },
        {
            Header: t('patients.column.regime'),
            accessor: "regime",
            filterable: { component: Select, options: getRegimeOptions() },
            Cell: (row) => (!row.regime || row.regime === "N/A")
                ? <span className="cell-muted">—</span>
                : <span style={{ fontSize: 13, fontWeight: 500, color: "#374151" }}>{row.regime}</span>,
        },
        {
            Header: t('patients.column.insurance'),
            accessor: "healthInsurance",
            filterable: { component: InputInsurance },
            Cell: (row) => row.healthInsurance
                ? (
                    <div className="d-flex align-items-center gap-2">
                        <img src={row.healthInsuranceLogo} style={{ width: 22, height: 22, borderRadius: 5, objectFit: "cover" }} alt="" />
                        <span style={{ fontSize: 13, fontWeight: 500, color: "#374151" }}>{row.healthInsuranceName}</span>
                    </div>
                )
                : <span className="cell-muted">—</span>,
        },
        {
            Header: t('Orders'),
            accessor: "ordersCount",
            sortable: true,
            className: 'text-center',
            Cell: (row) => (
                <span style={{
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    background: row.ordersCount > 0 ? "#eff6ff" : "#f9fafb",
                    color: row.ordersCount > 0 ? "#1d4ed8" : "#9ca3af",
                    fontWeight: 600, fontSize: 13,
                    padding: "3px 10px", borderRadius: 999, minWidth: 32,
                }}>
                    {row.ordersCount}
                </span>
            ),
        },
        {
            Header: t('patients.column.city'),
            accessor: "city",
            filterable: { component: InputText },
            Cell: (row) => {
                const addr = row.addresses?.find(a => a.default);
                return addr
                    ? <span style={{ fontSize: 13, fontWeight: 500, color: "#374151" }}>{addr.city}</span>
                    : <span className="cell-muted">—</span>;
            },
        },
        {
            Header: "",
            accessor: "actions",
            className: "col-actions",
            Cell: (row) => (
                <div className="cell-dropdown">
                    <button className="cell-actions-btn dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
                        <FontAwesomeIcon icon={faEllipsisH} />
                    </button>
                    {renderPatientActions(row)}
                </div>
            ),
        },
    ];

    const fabButtons = canCreate
        ? [{ id: 'patient', icon: faUserPlus, label: t('New Patient'), onClick: handleNewPatient }]
        : [];

    return (
        <>
            <EntityTable
                ref={tableRef}
                endpoint={`${API_BASE_URL}/api/v1/getPatients`}
                columns={columns}
                defaultSort="id"
                defaultDirection="desc"
                pageSize={20}
                emptyMessage={t('patients.empty')}
                searchable={true}
                multiSelect={true}
                selectionActions={canDelete ? [
                    {
                        label: t('Delete'),
                        icon: faTrash,
                        variant: "danger",
                        onClick: (ids) => requestDelete(ids),
                    },
                ] : []}
                searchPlaceholder={t('patients.search_placeholder')}
                enableCardView={true}
                renderCard={renderPatientCard}
            />

            <PatientModal
                patient={selectedPatient}
                show={showModal}
                onClose={() => setShowModal(false)}
                onSave={() => tableRef.current?.reload()}
            />

            {/* same dialog as the patient page's "Supprimer" (PatientDetails) */}
            <ConfirmModal
                id="deletePatientModal"
                show={showDeleteModal}
                title={pendingDelete.ids.length > 1 ? t('Supprimer les patients') : t('Supprimer le patient')}
                description={pendingDelete.ids.length > 1 ? (
                    t('Êtes-vous sûr de vouloir supprimer ces {{count}} patients ? Toutes leurs données (ventes, factures, paiements, retours, rendez-vous, adresses, fidélité) seront définitivement supprimées. Cette action est irréversible.', { count: pendingDelete.ids.length })
                ) : (
                    <>
                        {t('Êtes-vous sûr de vouloir supprimer ce patient ? Toutes ses données (ventes, factures, paiements, retours, rendez-vous, adresses, fidélité) seront définitivement supprimées. Cette action est irréversible.')}
                        {pendingDelete.patient?.unpaidOrdersCount > 0 && (
                            <>
                                <br /><br />
                                <strong style={{ color: '#991b1b' }}>
                                    {t('Attention')} : {t('ce patient a')} {pendingDelete.patient.unpaidOrdersCount} {pendingDelete.patient.unpaidOrdersCount > 1 ? t('ventes en attente') : t('vente en attente')} ({t('reste dû')} {formatPrice(pendingDelete.patient.remainingToPay)}).
                                </strong>
                            </>
                        )}
                    </>
                )}
                confirmLabel={t('Supprimer')}
                cancelLabel={t('Retour')}
                confirmVariant="danger"
                isLoading={isDeleting}
                onClose={closeDeleteModal}
                onConfirm={handleConfirmDelete}
            />

            <FabGroup buttons={fabButtons} />
        </>
    );
}
